import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { checkLoginStatus, fetchBinaryTree, fetchBinaryTreeNodeChildren } from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import { 
  BinaryLegsProps, 
  OrganizationChartNodeProps, 
  OrganizationChartNodeData 
} from './BinaryLegs.types';
import { adaptBinaryLegsProps } from './BinaryLegs.adapter';
import './BinaryLegs.css';

/**
 * 조직도 노드 컴포넌트
 */
const OrganizationChartNode: React.FC<OrganizationChartNodeProps> = ({ 
  node, 
  onClick, 
  onMoreClick,
  loadingNodes = new Set(),
  isRootNode = false, 
  lineColor = '#3f51b5' 
}) => {
  const { t } = useTranslation();
  
  if (!node) return null;
  
  // 자식 노드 확인
  const hasLeftChild = node.children && node.children.length > 0 && node.children[0];
  const hasRightChild = node.children && node.children.length > 1 && node.children[1];
  const hasAnyChild = hasLeftChild || hasRightChild;
  const hasSingleChild = (hasLeftChild && !hasRightChild) || (!hasLeftChild && hasRightChild);
  
  // 자식이 하나일 때 사용할 실제 자식 노드
  const singleChild = hasLeftChild ? node.children?.[0] : (hasRightChild ? node.children?.[1] : null);
  
  // node.class에 따라 타이틀 클래스 선택
  const titleClassName = `title ${node.class && node.class.includes('shipper') ? 'shipper-title' : 'normal-title'}`;
  
  // 선 스타일 설정
  const lineStyle = {
    backgroundColor: lineColor
  };
  
  return (
    <div className="node-wrapper">
      <table cellSpacing="0" cellPadding="0">
        <tbody>
          <tr>
            <td colSpan={2} align="center" style={{ position: 'relative' }}>
              <div 
                className={`node ${node.class || ''} ${isRootNode ? 'root-node' : ''}`} 
                onClick={() => onClick(node, isRootNode)}
              >
                <div className={titleClassName}>
                  {node.name || node.userId || ''}
                </div>
                <div 
                  className="content" 
                  style={node.backgroundColor ? { backgroundColor: node.backgroundColor } : undefined}
                >
                  {/* 동그라미 풍선 (totalPV, rank, grade) */}
                  {(() => {
                    const bubbleItems = [];
                    const pvValue = node.totalPV !== undefined && node.totalPV !== null ? 
                      node.totalPV : node.pv;
                    if (pvValue !== undefined && pvValue !== null) {
                      const formattedPV = pvValue.toLocaleString();
                      bubbleItems.push(`PV ${formattedPV}`);
                    }
                    if (node.rank) {
                      bubbleItems.push(node.rank);
                    }
                    if (node.grade) {
                      bubbleItems.push(node.grade);
                    }
                    
                    if (bubbleItems.length > 0) {
                      return (
                        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                          {bubbleItems.map((item, index) => (
                            <span key={index} className="bubble-badge">{item}</span>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* 텍스트 정보 - API에서 받은 데이터만 표시 */}
                  {(() => {
                    const textInfo = [];
                    
                    // userId 표시
                    if (node.userId) {
                      textInfo.push(`ID: ${node.userId}`);
                    }
                    
                    // name 표시 (userId와 다른 경우에만)
                    if (node.name && node.name !== node.userId) {
                      textInfo.push(`이름: ${node.name}`);
                    }
                    
                    // phone 표시
                    if (node.phone && node.phone.trim() !== '') {
                      textInfo.push(`전화: ${node.phone}`);
                    }
                    
                    // center 표시
                    if (node.center) {
                      textInfo.push(`센터: ${node.center}`);
                    }
                    
                    // groupPv 표시
                    if (node.groupPv !== undefined && node.groupPv !== null && node.groupPv > 0) {
                      const formattedGroupPV = node.groupPv.toLocaleString();
                      textInfo.push(`그룹PV: ${formattedGroupPV}`);
                    }
                    
                    if (textInfo.length > 0) {
                      return (
                        <div style={{ textAlign: 'left', fontSize: '11px', lineHeight: 1.4 }}>
                          {textInfo.map((info, index) => (
                            <div key={index}>{info}</div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </td>
          </tr>
          {/* 플러스 버튼: underMore가 true인 경우에만 표시 */}
          {node.underMore && (
            <tr>
              <td colSpan={2} align="center" style={{ paddingTop: 10 }}>
                <button 
                  className="more-button" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onMoreClick) {
                      onMoreClick(node.id);
                    }
                  }}
                  disabled={loadingNodes.has(node.id)}
                >
                  {loadingNodes.has(node.id) ? '...' : '+'}
                </button>
              </td>
            </tr>
          )}
          {hasAnyChild && (
            <>
              {/* 부모 노드에서 내려오는 세로선 */}
              <tr className="lines">
                <td colSpan={2} align="center" style={{ height: '25px', padding: 0 }}>
                  <div className="downLine" style={lineStyle}></div>
                </td>
              </tr>
              
              {/* 자식이 하나인 경우와 여러 개인 경우 분기 */}
              {hasSingleChild ? (
                // 자식이 하나인 경우 - 세로선만 표시
                <tr className="nodes">
                  <td colSpan={2} align="center" valign="top" className="single-child-container">
                    {singleChild && (
                      <OrganizationChartNode 
                        node={singleChild} 
                        onClick={onClick} 
                        onMoreClick={onMoreClick}
                        loadingNodes={loadingNodes}
                        isRootNode={false}
                        lineColor={lineColor}
                      />
                    )}
                  </td>
                </tr>
              ) : (
                // 자식이 두 개인 경우 - 가로선과 각각의 노드 표시
                <>
                  {/* 가로선과 세로선을 하나의 tr로 통합 */}
                  <tr className="lines">
                    <td className="rightLine" style={{ height: '25px', padding: 0 }}>
                      {hasLeftChild && <div className="child-connector" style={lineStyle}></div>}
                    </td>
                    <td className="leftLine" style={{ height: '25px', padding: 0 }}>
                      {hasRightChild && <div className="child-connector" style={lineStyle}></div>}
                    </td>
                  </tr>
                  <tr className="nodes">
                    <td align="center" valign="top" style={{ padding: '0 25px' }}>
                      {hasLeftChild && node.children && (
                        <OrganizationChartNode 
                          node={node.children[0]} 
                          onClick={onClick} 
                          onMoreClick={onMoreClick}
                          loadingNodes={loadingNodes}
                          isRootNode={false}
                          lineColor={lineColor}
                        />
                      )}
                    </td>
                    <td align="center" valign="top" style={{ padding: '0 25px' }}>
                      {hasRightChild && node.children && (
                        <OrganizationChartNode 
                          node={node.children[1]} 
                          onClick={onClick} 
                          onMoreClick={onMoreClick}
                          loadingNodes={loadingNodes}
                          isRootNode={false}
                          lineColor={lineColor}
                        />
                      )}
                    </td>
                  </tr>
                </>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

// 전역 중복 방지 맵 - 컴포넌트 인스턴스별로 관리
const globalFetchMap = new Map<string, boolean>();

/**
 * 바이너리 레그 메인 컴포넌트
 */
const BinaryLegsComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = adaptBinaryLegsProps(props);
  
  const { t } = useTranslation();
  
  const {
    id = '',
    style = {},
    className = 'binary-legs-component',
    componentProps = {},
    ...rest
  } = adaptedProps;
  
  // HTML 아닌 속성들 제외
  const { content, pathVariables, data, ...divProps } = rest;
  
  // Redux 타입 에러 해결을 위해 any 타입 단언 사용
  const dispatch = useDispatch() as any;
  
  // 리덕스 상태 select
  const user = useSelector((state: any) => state.withcookie.user);
  const binaryTreeData = useSelector((state: any) => state.withcookie.binaryTreeData);
  const binaryTreeLoading = useSelector((state: any) => state.withcookie.binaryTreeLoading);
  const binaryTreeError = useSelector((state: any) => state.withcookie.binaryTreeError);
  
  // withcookieData에서 테마 색상 가져오기
  const withcookieData = useSelector((state: any) => state.withcookie.withcookieData);
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#3f51b5";
  const secondaryColor = colorSet?.secondary || "#e91e63";
  
  // 버튼 스타일 설정
  const buttonStyle = {
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '5px 15px',
    marginRight: '10px',
    marginBottom: '10px',
    cursor: 'pointer',
    fontSize: '14px'
  };
  
  // 비활성화된 버튼 스타일
  const disabledButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
    opacity: 0.7
  };
  
  const [currentUserId, setCurrentUserId] = useState<string | number | null>(null);
  const [debugMode, setDebugMode] = useState<boolean>(
    typeof componentProps.debugMode !== 'undefined' ? componentProps.debugMode : true
  );
  const [mockingEnabled, setMockingEnabled] = useState<boolean>(false);
  const [customApiUrl, setCustomApiUrl] = useState<string>('');
  const [nodeHistory, setNodeHistory] = useState<Array<string | number>>([]);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const minZoom = 0.3;
  const maxZoom = 2.5;
  const [offset, setOffset] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const lastPos = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
  const lastTouchDist = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  
  // 사용자 조작 상태 추적을 위한 새로운 state 추가
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  
  // 로딩 중인 노드 ID 추적
  const [loadingNodes, setLoadingNodes] = useState<Set<string | number>>(new Set());
  
  
  // 중앙 정렬 함수 - 초기 로드 시에만 사용
  const centerChart = useCallback(() => {
    if (!containerRef.current || hasUserInteracted) return;
    
    const container = containerRef.current;
    const orgChart = container.querySelector('.orgchart') as HTMLElement;
    
    if (!orgChart) return;
    
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    // 차트의 현재 크기 (줌 적용된 상태)
    const chartRect = orgChart.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    const chartWidth = chartRect.width;
    const chartHeight = chartRect.height;
    
    // 컨테이너 중앙 계산
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;
    
    // 차트를 컨테이너 중앙에 위치시키기 위한 offset 계산
    const chartCenterX = chartWidth / 2;
    const offsetX = centerX - chartCenterX;
    
    // y 방향은 루트 노드가 적절한 위치에 오도록 조정
    let offsetY;
    if (chartHeight > containerHeight * 0.9) {
      offsetY = 30;
    } else {
      offsetY = containerHeight * 0.25;
    }
    
    setOffset({
      x: offsetX,
      y: offsetY
    });
    
    // 초기 로드 완료 표시
    setIsInitialLoad(false);
  }, [hasUserInteracted]);

  // 마우스 위치 기반 줌 함수 - 완전히 새로운 접근 방식
  const zoomAtPoint = useCallback((newZoom: number, clientX: number, clientY: number) => {
    if (!containerRef.current || newZoom === zoomLevel) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    // 컨테이너 기준 마우스/터치 위치
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;
    
    // 줌 비율 계산
    const scale = newZoom / zoomLevel;
    
    // 현재 오프셋에서 마우스 위치를 기준으로 새로운 오프셋 계산
    // 마우스 포인터가 가리키는 지점이 줌 후에도 같은 화면 위치에 있도록 조정
    const newOffsetX = mouseX - (mouseX - offset.x) * scale;
    const newOffsetY = mouseY - (mouseY - offset.y) * scale;
    
    // 상태를 함수형 업데이트로 동시에 적용
    setZoomLevel(newZoom);
    setOffset({
      x: newOffsetX,
      y: newOffsetY
    });
  }, [zoomLevel, offset]);

  // 임시 모킹 데이터 생성 (API가 동작하지 않을 때 테스트용)
  const generateMockData = useCallback((rootUserId: string | number): OrganizationChartNodeData => {
    const rootId = rootUserId || (user?.id || 1);
    return {
      id: rootId,
      name: `사용자 ${rootId}`,
      title1: `${t('성함')}: ${user?.name || `테스트 사용자 ${rootId}`}`,
      title2: `${t('센터')}: 테스트 센터`,
      class: 'shipper',
      backgroundColor: '#6D81F8',
      level2: '3PS',
      totalPV: 500,
      children: [
        {
          id: Number(rootId) + 1,
          name: `사용자 ${Number(rootId) + 1}`,
          title1: `${t('성함')}: 테스트 사용자 ${Number(rootId) + 1}`,
          title2: `${t('센터')}: 왼쪽 센터`,
          class: '',
          backgroundColor: '#BAF855',
          level2: '2PS',
          totalPV: 1000,
          children: []
        },
        {
          id: Number(rootId) + 2,
          name: `사용자 ${Number(rootId) + 2}`,
          title1: `${t('성함')}: 테스트 사용자 ${Number(rootId) + 2}`,
          title2: `${t('센터')}: 오른쪽 센터`,
          class: '',
          backgroundColor: '#FF6B6B',
          level2: '1PS',
          totalPV: undefined, // 일부러 없는 경우 테스트
          children: []
        }
      ]
    };
  }, [user, t]);

  // 노드 클릭 핸들러
  const handleNodeClick = useCallback((nodeData: OrganizationChartNodeData, isRootNode = false) => {
    
    // 현재 노드가 클릭한 노드와 같으면서 루트 노드가 아닌 경우만 무시
    // 루트 노드인 경우는 상위 노드 조회를 위해 무시하지 않음
    if (String(currentUserId) === String(nodeData.id) && !isRootNode) {
      return;
    }
    
    // 같은 루트 노드 클릭 시 상위 노드 조회를 위해 계속 진행
    if (String(currentUserId) === String(nodeData.id) && isRootNode) {
    }
    
    // 현재 노드를 히스토리에 추가 (현재 ID가 있을 경우에만, 그리고 같은 노드가 아닌 경우만)
    if (currentUserId !== null && String(currentUserId) !== String(nodeData.id)) {
      
      // 히스토리 업데이트가 비동기적이므로 이전 상태 기반으로 업데이트
      setNodeHistory(prev => {
        const newHistory = [...prev, currentUserId];
        return newHistory;
      });
    }
    
    // 새 노드 ID로 변경 (같은 노드 클릭 시에는 변경하지 않음)
    if (String(currentUserId) !== String(nodeData.id)) {
      setCurrentUserId(nodeData.id);
    }
    
    // API 파라미터 준비
    const apiParams = {
      id: String(nodeData.id),
      depth: "6"
    };
    
    // 최상단 노드 클릭 확인
    // 1. isRootNode가 true이면 현재 표시된 트리의 루트 노드
    // 2. 현재 표시된 데이터의 ID와 클릭한 노드의 ID가 같으면 동일 노드 재클릭
    // 3. binaryTreeData가 없거나 첫 로드 시에도 one_upper 필요
    const isTopLevelNode = isRootNode || 
                           (binaryTreeData && String(binaryTreeData.id) === String(nodeData.id)) ||
                           !binaryTreeData;
    
    // 루트 노드 클릭 또는 재클릭 시 항상 one_upper 파라미터 추가
    if (isTopLevelNode || isRootNode) {
      (apiParams as any).one_upper = true;
    }
    
    
    // API 호출 - 객체 직접 전달
    dispatch(fetchBinaryTree({...apiParams}));
  }, [dispatch, currentUserId, binaryTreeData]);


  // 이전 노드로 돌아가는 함수
  const goToPreviousNode = useCallback(() => {
    
    if (!nodeHistory || nodeHistory.length === 0) {
      return;
    }
    
    // 히스토리에서 마지막 노드 ID 가져오기
    const prevNodeId = nodeHistory[nodeHistory.length - 1];
    
    // 히스토리에서 마지막 항목 제거하고 상태 업데이트
    setNodeHistory(nodeHistory.slice(0, -1));
    
    // 이전 노드로 이동
    setCurrentUserId(prevNodeId);
    
    // 이전 노드가 현재 사용자의 루트 노드인지 확인
    const isUserRootNode = user && user.id && String(prevNodeId) === String(user.id);
    
    const apiParams = {
      id: String(prevNodeId),
      depth: "8"
    };
    
    // 사용자의 루트 노드로 이동하는 경우에는 one_upper를 true로 설정
    if (isUserRootNode) {
      (apiParams as any).one_upper = true;
    }
    
    // 데이터 요청
    dispatch(fetchBinaryTree({...apiParams}));
  }, [nodeHistory, dispatch, user]);

  // 루트 노드로 돌아가는 함수
  const goToRootNode = useCallback(() => {
    
    if (!user || !user.id) {
      return;
    }
    
    // 현재 표시 중인 노드가 이미 루트 노드인 경우 아무 작업도 수행하지 않음
    if (String(currentUserId) === String(user.id)) {
      return;
    }
    
    // 히스토리 초기화
    setNodeHistory([]);
    
    // 루트 노드(로그인한 사용자)로 이동
    setCurrentUserId(user.id);
    
    const apiParams = {
      id: String(user.id),
      depth: "8",
      one_upper: true
    };
    
    // 루트 노드 데이터 요청
    dispatch(fetchBinaryTree({...apiParams}));
  }, [user, dispatch, currentUserId]);

  // API 호출 중복 방지 ref - 전역 중복 방지
  const fetchInProgressRef = useRef(false);
  const lastFetchParamsRef = useRef<string>('');
  const renderCountRef = useRef(0);
  const mountedRef = useRef(true);
  
  // 컴포넌트 언마운트 감지
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      fetchInProgressRef.current = false;
    };
  }, []);

  // 렌더링 횟수 추적
  renderCountRef.current += 1;
  console.log(`[BinaryLegs] 렌더링 #${renderCountRef.current}`, {
    user: !!user,
    currentUserId,
    binaryTreeLoading,
    binaryTreeData: !!binaryTreeData
  });

  // 초기 데이터 로드 - 중복 방지 로직 적용
  useEffect(() => {
    console.log(`[BinaryLegs] checkLoginStatus useEffect 실행`, {
      fetchInProgress: fetchInProgressRef.current
    });
    
    // 현재 호출 파라미터 생성
    const currentParams = JSON.stringify({ type: 'checkLoginStatus' });
    const globalKey = `binary-legs-auth-${currentParams}`;
    
    // 전역 중복 방지 체크
    if (globalFetchMap.get(globalKey)) {
      console.log(`[BinaryLegs] 전역 중복 방지 - checkLoginStatus 스킵`, globalKey);
      return;
    }
    
    // 로컬 중복 방지 체크
    if (fetchInProgressRef.current) {
      console.log(`[BinaryLegs] 로컬 호출 중 - checkLoginStatus 스킵`);
      return;
    }
    
    if (lastFetchParamsRef.current === currentParams) {
      console.log(`[BinaryLegs] 같은 파라미터로 이미 호출함 - checkLoginStatus 스킵`, currentParams);
      return;
    }
    
    // API 호출 시작 표시 (전역 + 로컬)
    globalFetchMap.set(globalKey, true);
    fetchInProgressRef.current = true;
    lastFetchParamsRef.current = currentParams;
    
    console.log(`[BinaryLegs] checkLoginStatus API 호출 시작`, currentParams);
    
    const checkAuth = async () => {
      try {
        // 사용자 인증 상태 확인
        await dispatch(checkLoginStatus({ forceRefresh: true }));
      } finally {
        // 호출 완료 후 플래그 정리
        if (mountedRef.current) {
          fetchInProgressRef.current = false;
        }
        globalFetchMap.delete(globalKey);
        console.log(`[BinaryLegs] checkLoginStatus API 호출 완료`, currentParams);
      }
    };
    
    checkAuth();
  }, [dispatch]);

  // 사용자 정보가 로드되면 해당 사용자 ID로 바이너리 트리 로드
  useEffect(() => {
    if (user && !binaryTreeData && !binaryTreeLoading) {
      
      // 초기 로드시에만 currentUserId 설정 및 히스토리 초기화
      // 이미 currentUserId가 설정되어 있으면 초기화하지 않음
      if (currentUserId === null) {
        setCurrentUserId(user.id);
        setNodeHistory([]);
        
        // API 호출
        dispatch(fetchBinaryTree({ 
          id: String(user.id),
          depth: "8",
          one_upper: true 
        }));
      }
    }
  }, [user, dispatch, binaryTreeData, binaryTreeLoading, currentUserId]);

  // binaryTreeData가 업데이트될 때 초기 로드시에만 중앙으로 정렬
  useEffect(() => {
    if (binaryTreeData && !binaryTreeLoading && isInitialLoad && !hasUserInteracted) {
      // DOM 렌더링이 완료될 때까지 충분한 지연을 두고 중앙 정렬
      const timer = setTimeout(() => {
        centerChart();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [binaryTreeData, binaryTreeLoading, isInitialLoad, hasUserInteracted, centerChart]);

  // 새로운 노드로 이동할 때만 중앙 정렬 (사용자가 아직 조작하지 않은 경우)
  useEffect(() => {
    if (currentUserId !== null && binaryTreeData && !binaryTreeLoading) {
      // 노드 변경 시에는 사용자 조작 상태를 리셋하고 중앙 정렬
      setHasUserInteracted(false);
      setIsInitialLoad(true);
    }
  }, [currentUserId, binaryTreeData, binaryTreeLoading]);

  // 모킹 모드 전환 시 데이터 업데이트
  const enableMockData = useCallback(() => {
    setMockingEnabled(true);
  }, []);

  // 임시 데이터 로드
  useEffect(() => {
    if (mockingEnabled && user) {
      generateMockData(user.id);
    }
  }, [mockingEnabled, user, generateMockData]);

  // 실제 API로 다시 시도
  const retryWithRealApi = useCallback(() => {
    if (user && user.id) {
      setMockingEnabled(false);
      dispatch(fetchBinaryTree({ id: String(user.id) }));
    }
  }, [user, dispatch]);

  // 마우스 휠 이벤트 핸들러 - 디버깅 로그 추가
  const handleWheel = useCallback((e: React.WheelEvent) => {
    // 사용자 조작 표시
    setHasUserInteracted(true);
    
    // 이벤트 기본 동작 방지
    e.preventDefault();
    e.stopPropagation();
    
    // 확대/축소 방향과 속도 결정
    const zoomSpeed = 0.15;
    let delta = e.deltaY < 0 ? zoomSpeed : -zoomSpeed;
    
    // Ctrl 키를 누르고 있으면 더 세밀하게 조절
    if (e.ctrlKey) {
      delta *= 0.5;
    }
    
    // 새 줌 레벨 계산
    const newZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel + delta));
    
    if (newZoom !== zoomLevel) {
      // 마우스 커서 위치를 정확히 기준으로 줌 적용
      zoomAtPoint(newZoom, e.clientX, e.clientY);
    }
  }, [zoomLevel, zoomAtPoint]);

  // 마우스 드래그 이벤트 핸들러 개선
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // 사용자 조작 표시
    setHasUserInteracted(true);
    
    // 좌클릭(0)일 때만 드래그 시작
    if (e.button === 0) {
      setIsDragging(true);
      lastPos.current = { x: e.clientX, y: e.clientY };
      
      // 드래그 모드에서 커서 변경
      const container = e.currentTarget as HTMLElement;
      if (container) {
        container.style.cursor = 'grabbing';
      }
      
      // 텍스트 선택 방지
      e.preventDefault();
    }
  }, []);
  
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    
    // 오프셋 업데이트
    setOffset(prev => ({
      x: prev.x + dx,
      y: prev.y + dy
    }));
    
    // 마지막 위치 업데이트
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, [isDragging]);
  
  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setIsDragging(false);
      
      // 드래그 종료 시 커서 원복
      const container = e.currentTarget as HTMLElement;
      if (container) {
        container.style.cursor = 'grab';
      }
    }
  }, [isDragging]);
  
  // 터치 이벤트 핸들러 개선
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // 사용자 조작 표시
    setHasUserInteracted(true);
    
    // 기본 동작 방지 (스크롤 등)
    e.preventDefault();
    
    if (e.touches.length === 2) {
      // 핀치 줌 제스처 시작
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.sqrt(dx * dx + dy * dy);
      
      // 두 터치 포인트의 중심점을 저장 (줌 중심점으로 사용)
      const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      lastPos.current = { x: centerX, y: centerY };
      
      // 드래그 상태 리셋
      setIsDragging(false);
    } else if (e.touches.length === 1) {
      // 일반 드래그 시작
      setIsDragging(true);
      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, []);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    
    if (e.touches.length === 2 && lastTouchDist.current) {
      // 핀치 줌 제스처 진행 중
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      const newDist = Math.sqrt(dx * dx + dy * dy);
      
      // 거리 변화가 충분할 때만 줌 적용
      if (Math.abs(newDist - lastTouchDist.current) > 5) {
        // 현재 두 터치 포인트의 중심점 계산
        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        
        // 줍 변화량을 거리 비율로 계산
        const scaleFactor = newDist / lastTouchDist.current;
        
        // 줌 변화량 제한 (너무 급격한 변화 방지)
        const clampedScale = Math.max(0.9, Math.min(1.1, scaleFactor));
        const newZoom = Math.min(maxZoom, Math.max(minZoom, zoomLevel * clampedScale));
        
        if (newZoom !== zoomLevel) {
          // 두 손가락의 중심점을 기준으로 줌 적용
          zoomAtPoint(newZoom, centerX, centerY);
        }
        
        lastTouchDist.current = newDist;
      }
    } else if (e.touches.length === 1 && isDragging) {
      // 일반 드래그 진행 중
      const dx = e.touches[0].clientX - lastPos.current.x;
      const dy = e.touches[0].clientY - lastPos.current.y;
      
      setOffset(prev => ({
        x: prev.x + dx,
        y: prev.y + dy
      }));
      
      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, [isDragging, zoomLevel, zoomAtPoint]);
  
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    lastTouchDist.current = null;
    
    // 모든 터치가 끝났을 때만 드래그 상태 리셋
    if (e.touches.length === 0) {
      setIsDragging(false);
    }
  }, []);

  // 수동 중앙 정렬 함수 (버튼용)
  const manualCenterChart = useCallback(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const orgChart = container.querySelector('.orgchart') as HTMLElement;
    
    if (!orgChart) return;
    
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    // 차트의 현재 크기 (줌 적용된 상태)
    const chartRect = orgChart.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    const chartWidth = chartRect.width;
    const chartHeight = chartRect.height;
    
    // 컨테이너 중앙 계산
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;
    
    // 차트를 컨테이너 중앙에 위치시키기 위한 offset 계산
    const chartCenterX = chartWidth / 2;
    const offsetX = centerX - chartCenterX;
    
    let offsetY;
    if (chartHeight > containerHeight * 0.9) {
      offsetY = 30;
    } else {
      offsetY = containerHeight * 0.25;
    }
    
    setOffset({
      x: offsetX,
      y: offsetY
    });
  }, []);

  // 줌 리셋 함수
  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setHasUserInteracted(false); // 리셋 후 자동 중앙 정렬 허용
    setTimeout(() => {
      manualCenterChart();
    }, 50);
  }, [manualCenterChart]);


  // 플러스 버튼 클릭 핸들러 (데이터 로딩)
  const handleMoreButtonClick = useCallback(async (nodeId: string | number) => {
    console.log('[바이너리 레그] + 버튼 클릭, nodeId:', nodeId);
    
    // 로딩 상태 시작
    setLoadingNodes(prev => new Set(prev).add(nodeId));
    
    try {
      // fetchBinaryTreeNodeChildren 액션 사용하여 부분 업데이트
      await dispatch(fetchBinaryTreeNodeChildren({
        id: String(nodeId),
        depth: "8"
      })).unwrap();
      
      console.log('[바이너리 레그] 추가 데이터 로드 완료');
    } catch (error) {
      console.error('[바이너리 레그] 추가 데이터 로드 오류:', error);
    } finally {
      // 로딩 상태 종료
      setLoadingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
    }
  }, [dispatch]);


  // 디버그 모드 컨트롤
  const toggleDebugMode = useCallback(() => {
    setDebugMode(!debugMode);
  }, [debugMode]);

  // 타이틀 구성
  const title = componentProps?.title ? t(componentProps.title) : t('조직도');
  const emptyText = componentProps?.emptyText ? t(componentProps.emptyText) : t('데이터가 없습니다');
  const loadingText = componentProps?.loadingText ? t(componentProps.loadingText) : t('조직도를 불러오는 중입니다...');
  const errorText = componentProps?.errorText ? t(componentProps.errorText) : t('데이터를 불러오는데 실패했습니다');
  const loginRequiredText = componentProps?.loginRequiredText ? t(componentProps.loginRequiredText) : t('로그인이 필요한 서비스입니다.');

  return (
    <div className={className} style={style} {...divProps}>
      <h2 className="mb-4 d-flex">
        {title}
        <button 
          onClick={toggleDebugMode} 
          style={{marginLeft: 'auto', background: 'none', border: 'none', fontSize: '0.8rem', color: '#666'}}
          title={t("디버그 모드 전환")}
        >
          🛠️
        </button>
      </h2>
      
      {/* 네비게이션 버튼 추가 */}
      <div className="binary-navigation-controls">
        <button 
          style={currentUserId && nodeHistory.length > 0 ? buttonStyle : disabledButtonStyle}
          onClick={goToPreviousNode}
          disabled={!currentUserId || nodeHistory.length === 0}
          title={t("이전으로 돌아가기")}
        >
          {t("이전으로")}
        </button>
        <button 
          style={currentUserId && String(currentUserId) !== String(user?.id) ? buttonStyle : disabledButtonStyle}
          onClick={goToRootNode}
          disabled={!currentUserId || String(currentUserId) === String(user?.id)}
          title={t("처음으로 돌아가기")}
        >
          {t("처음으로")}
        </button>
        {debugMode && (
          <>
            <button 
              style={{...buttonStyle, backgroundColor: '#28a745'}}
              onClick={enableMockData}
              title={t("테스트 데이터 생성")}
            >
              {t("테스트 데이터")}
            </button>
            <button 
              style={{...buttonStyle, backgroundColor: '#dc3545'}}
              onClick={retryWithRealApi}
              title={t("실제 API로 다시 시도")}
            >
              {t("API 재시도")}
            </button>
          </>
        )}
      </div>
      
      {currentUserId && currentUserId !== user?.id && (
        <div className="navigation-breadcrumb">
          <span className="breadcrumb-item root" onClick={goToRootNode}>{t("내 레그")}</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item current">
            {binaryTreeData?.name || `ID: ${currentUserId}`}
          </span>
        </div>
      )}
      
      <div 
        ref={containerRef}
        className="orgchart-container" 
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          overflow: 'hidden',
          touchAction: 'none',
          WebkitOverflowScrolling: 'touch',
          cursor: isDragging ? 'grabbing' : 'grab',
          backgroundColor: 'white',
        }}
      >
        {binaryTreeLoading && <div className="loading-indicator">{loadingText}</div>}
        {binaryTreeError && (
          <div className="error-message">
            <p>{errorText}</p>
            {debugMode && (
              <div className="debug-info" style={{backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px', marginTop: '10px', fontSize: '12px', textAlign: 'left'}}>
                <h4>{t("디버그 정보")}</h4>
                <p>{t("현재 위치")}: {window.location.href}</p>
                <p>{t("호스트")}: {window.location.hostname}</p>
                <p>{t("사용자 ID")}: {currentUserId}</p>
                {user && (
                  <div>
                    <h5>{t("로그인 사용자 정보")}</h5>
                    <p>{t("이름")}: {user.name} (ID: {user.id})</p>
                    <p>{t("회원유형")}: {user.role}</p>
                    <p>binaryPosition: {user.binaryPosition || t('없음')}</p>
                    <p>{t("추천인코드")}: {user.referralCode || t('없음')}</p>
                  </div>
                )}
                <p>{t("문제 해결 방법")}:</p>
                <ol>
                  <li>{t("개발자 도구(F12)의 콘솔 및 네트워크 탭에서 API 호출 오류를 확인하세요.")}</li>
                  <li>{t("404 오류는 API 엔드포인트가 존재하지 않는다는 의미입니다. 백엔드 개발자에게 정확한 API 경로를 확인하세요.")}</li>
                </ol>
                
                <h5>{t("디버그 - 조직도 옵션")}</h5>
                <p>one_upper 파라미터: 현재 최상위 노드보다 한 단계 위의 노드까지 조회</p>
                <p>isRootNode 값: {binaryTreeData ? '현재 표시된 노드는 루트 노드입니다' : '로드 중...'}</p>
                
                <h5>{t("직접 테스트하기")}</h5>
                <div style={{marginBottom: '10px'}}>
                  <label style={{display: 'block', marginBottom: '5px'}}>
                    <input 
                      type="checkbox" 
                      checked={mockingEnabled}
                      onChange={(e) => setMockingEnabled(e.target.checked)}
                    /> 
                    {t("임시 모킹 데이터 사용 (API 대신 테스트 데이터 사용)")}
                  </label>
                </div>
                
                {!mockingEnabled && (
                  <div>
                    <div style={{marginBottom: '10px'}}>
                      <label style={{display: 'block', marginBottom: '5px'}}>{t("API 호스트")}:</label>
                      <input 
                        type="text" 
                        value={customApiUrl} 
                        onChange={(e) => setCustomApiUrl(e.target.value)}
                        placeholder={t("예: https://nesem.io.kr")}
                        style={{width: '100%', padding: '5px'}}
                      />
                    </div>
                    <button 
                      onClick={retryWithRealApi}
                      style={{padding: '5px 10px', backgroundColor: primaryColor, color: 'white', border: 'none', borderRadius: '4px'}}
                    >
                      {t("API 다시 시도")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {!binaryTreeLoading && !binaryTreeError && binaryTreeData && (
          <div 
            className="orgchart"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoomLevel})`,
              transformOrigin: '0 0',
              transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              display: 'inline-block',
            }}
          >
            <OrganizationChartNode 
              node={mockingEnabled && currentUserId ? generateMockData(currentUserId) : binaryTreeData} 
              onClick={handleNodeClick}
              onMoreClick={handleMoreButtonClick}
              loadingNodes={loadingNodes}
              isRootNode={true}
              lineColor={componentProps.lineColor || primaryColor}
            />
          </div>
        )}
        {!binaryTreeLoading && !binaryTreeError && !binaryTreeData && (
          <div className="empty-message">
            {loginRequiredText}
            {debugMode && user && (
              <div style={{marginTop: '15px', fontSize: '12px', color: '#666'}}>
                <p>{t("사용자 정보는 있지만 바이너리 레그 데이터를 가져오지 못했습니다.")}</p>
                <p>{t("콘솔에서 API 요청 로그를 확인하세요.")}</p>
                
                <div style={{marginTop: '15px'}}>
                  <button 
                    onClick={enableMockData}
                    style={{padding: '5px 10px', backgroundColor: secondaryColor, color: 'white', border: 'none', borderRadius: '4px', marginRight: '10px'}}
                  >
                    {t("임시 데이터로 테스트")}
                  </button>
                  <button 
                    onClick={retryWithRealApi}
                    style={{padding: '5px 10px', backgroundColor: primaryColor, color: 'white', border: 'none', borderRadius: '4px'}}
                  >
                    {t("API 다시 시도")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BinaryLegsComponent; 